import { useEffect, useState, useRef } from "react"
import { auth, db } from "../firebase"
import { signOut } from "firebase/auth"
import { doc, getDoc, updateDoc } from "firebase/firestore"
import { useNavigate } from "react-router-dom"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { LuRss, LuTarget, LuSettings, LuLogOut, LuChartBar, LuGrid3X3, LuCompass } from "react-icons/lu"
import "./Dashboard.css"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

function Dashboard() {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState("today")
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  const navigate = useNavigate()
  const lastTickRef = useRef(null)
  const bufferedSecondsRef = useRef(0)

  useEffect(() => {
    const init = async () => {
      const currentUser = auth.currentUser

      if (!currentUser) {
        navigate("/login", { replace: true })
        return
      }

      const snap = await getDoc(doc(db, "users", currentUser.uid))

      if (!snap.exists() || snap.data().informationProvided !== true) {
        navigate("/dashboard/about-you", { replace: true })
        return
      }

      setUser(currentUser)
      setUserData(snap.data())
      setLoading(false)
      lastTickRef.current = Date.now()
    }

    init()
  }, [navigate])

  useEffect(() => {
    if (!user) return

    const userRef = doc(db, "users", user.uid)

    const flushTime = async () => {
      if (!lastTickRef.current) return

      const now = Date.now()
      const diffSeconds = Math.floor((now - lastTickRef.current) / 1000)
      if (diffSeconds <= 0) return

      bufferedSecondsRef.current += diffSeconds
      lastTickRef.current = now

      if (bufferedSecondsRef.current < 30) return

      const minutesToSave = bufferedSecondsRef.current / 60
      bufferedSecondsRef.current = 0

      const todayKey = new Date().toLocaleDateString("en-CA")

      const snap = await getDoc(userRef)
      if (!snap.exists()) return

      const data = snap.data()
      const screen = data.screentime || {}

      const updatedScreen = {
        ...screen,
        [todayKey]: parseFloat(
          ((screen[todayKey] || 0) + minutesToSave).toFixed(2)
        )
      }

      const updatedTotal = parseFloat(
        ((data.totalScreenTime || 0) + minutesToSave).toFixed(2)
      )

      await updateDoc(userRef, {
        screentime: updatedScreen,
        totalScreenTime: updatedTotal
      })

      setUserData(prev => ({
        ...prev,
        screentime: updatedScreen,
        totalScreenTime: updatedTotal
      }))
    }

    const visibilityHandler = () => {
      lastTickRef.current = Date.now()
    }

    document.addEventListener("visibilitychange", visibilityHandler)
    const interval = setInterval(flushTime, 30000)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", visibilityHandler)
    }
  }, [user])

  const handleLogout = async () => {
    setLoggingOut(true)
    setTimeout(async () => {
      await signOut(auth)
      navigate("/login", { replace: true })
    }, 2000)
  }

  if (loading || !user || !userData) return null

  const screenData = userData.screentime || {}
  const todayDate = new Date()
  const todayKey = todayDate.toLocaleDateString("en-CA")

  let labels = []
  let values = []

  if (viewMode === "today") {
    const formattedDate = todayDate.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short"
    })
    labels = [`Today (${formattedDate})`]
    values = [screenData[todayKey] || 0]
  } else {
    for (let i = 6; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      const key = d.toLocaleDateString("en-CA")

      const formatted = d.toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short"
      })

      labels.push(formatted)
      values.push(screenData[key] || 0)
    }
  }

  const weeklyGoalMap = {
    "Less than 5 hrs": 5,
    "5–10 hrs": 10,
    "10–20 hrs": 20,
    "20+ hrs": 30
  }

  const weeklyTarget = weeklyGoalMap[userData.weeklyCommitment] || 10
  const dailyTarget = Math.round((weeklyTarget * 60) / 7)

  const chartData = {
    labels,
    datasets: [
      {
        label: "Minutes Spent",
        data: values,
        backgroundColor: "#6366f1",
        borderRadius: 10
      },
      {
        label: "Daily Target",
        data: labels.map(() => dailyTarget),
        backgroundColor: "#22c55e",
        borderRadius: 10
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 600 },
    plugins: { legend: { position: "top" } },
    scales: { y: { beginAtZero: true } }
  }

  return (
    <div className="dashboard-container">
      <div className="top-panels">
        <div className="profile-panel">
          <h2><span style={{background: 'linear-gradient(45deg, #667eea, #764ba2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>{userData.name}</span></h2>
          <p><strong>Branch:</strong> {userData.branch}</p>
          <p><strong>Year:</strong> {userData.year}</p>
          <p><strong>Skill Level:</strong> {userData.skillLevel}</p>
          <p><strong>College:</strong> {userData.college}</p>
          <p><strong>Goal:</strong> {userData.careerGoal}</p>
          <p><strong>Total Minutes:</strong> {(userData.totalScreenTime || 0).toFixed(2)}</p>

          {!confirmLogout ? (
            <button className="logout-btn" onClick={() => setConfirmLogout(true)}>
              <LuLogOut /> Logout
            </button>
          ) : (
            <div className="logout-confirm-box">
              <span>Are you sure?</span>
              <button className="yes-btn" onClick={handleLogout} disabled={loggingOut}>
                {loggingOut ? <div className="mini-loader"></div> : "Yes"}
              </button>
              <button className="no-btn" onClick={() => setConfirmLogout(false)} disabled={loggingOut}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="analytics-panel">
          <div className="chart-header">
            <h3><LuChartBar /> Activity Tracker</h3>
            <button
              className="toggle-btn"
              onClick={() => setViewMode(viewMode === "today" ? "week" : "today")}
            >
              <LuChartBar /> Toggle View
            </button>
          </div>

          <div className="chart-wrapper">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>

      <div className="quick-menu-panel">
        <h3><LuGrid3X3 /> Quick Menu</h3>
        <div className="quick-buttons">
          <button className="quick-btn" onClick={() => navigate("/dashboard/feed")}>
            <LuRss /> Feed
          </button>
          <button className="quick-btn" onClick={() => navigate("/dashboard/goal")}>
            <LuTarget /> Goals
          </button>
          <button className="quick-btn" onClick={() => navigate("/dashboard/explore")}>
            <LuCompass /> Explore
          </button>
          <button className="quick-btn">
            <LuSettings /> Settings
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard